import * as Yup from 'yup';
import _ from 'lodash';
import moment from 'moment-timezone';
import _get from 'lodash/get';
import _isFinite from 'lodash/isFinite';
import {
  Weight,
  buildInventoryDonationPrefill,
  toDonationInventoryPayload,
  validateEndTimeAfterStartTime,
} from 'careit';
import { parseUTC } from 'src/utils/timezones';
import {
  foodTypeWeightSchema,
  requireAtLeastOneFoodType,
} from 'src/hooks/useForm';
import { FOOD_TYPES } from 'src/constants';
import { useMeasureFieldUnits } from 'src/components/MeasureField';
import useAuth from './useAuth';
import useCheckCompletedOnboarding from 'src/hooks/useCheckCompletedOnboarding';
import { useTranslation } from 'react-i18next';
import { parseJsonArray, parseJsonObject } from 'src/utils/json';

// ----------------------------------------------------------------------

const getRecipientOnboardingState = ({
  teamId,
  nearbyNPOs = [],
  recipientStatusByTeamId = {},
} = {}) => {
  if (!teamId || teamId === 'unassigned') {
    return null;
  }

  return (
    recipientStatusByTeamId?.[teamId] ??
    nearbyNPOs.find((team) => team.team_id === teamId)?.onboarding ??
    null
  );
};

export function calculateDatesForCopy(
  startDate,
  endDate,
  user,
  isCopy,
  patternData,
  donationTimezone
) {
  // When copying a donation, use the donation's timezone for parsing dates
  // This prevents issues when admin's user.timezone_id is NULL and falls back to browser local time
  const timezoneOverride = isCopy ? donationTimezone : undefined;

  const parsedEndDate = parseUTC(endDate, user, undefined, timezoneOverride);

  // Calculate new dates for copy with patternData
  let dateObj = {};
  let calculatedStartDate, calculatedEndDate;
  if (isCopy && patternData) {
    // Calculate the number of days between start_date and end_date in patternData
    const originalStartDate = moment(patternData.start_date);
    const originalEndDate = moment(patternData.end_date);
    const daysDifference = originalEndDate.diff(originalStartDate, 'days');

    // Set new start date to today and end date to n days from start date
    calculatedStartDate = new Date();
    calculatedEndDate = moment(calculatedStartDate)
      .add(daysDifference, 'days')
      .toDate();
    dateObj = {
      startDate: calculatedStartDate,
      endDate: patternData?.end_date ? calculatedEndDate : null,
      startTime: parseUTC(startDate, user, undefined, timezoneOverride),
      endTime: parseUTC(endDate, user, undefined, timezoneOverride),
    };
  } else {
    dateObj = {
      startDate: parseUTC(startDate, user, undefined, timezoneOverride),
      endDate: parsedEndDate,
      startTime: parseUTC(startDate, user, undefined, timezoneOverride),
      endTime: parseUTC(endDate, user, undefined, timezoneOverride),
    };
  }

  return dateObj;
}

const rebaseDateRangeToToday = (startDate, endDate, timezoneId) => {
  const dayDiff =
    startDate && endDate
      ? moment(endDate)
          .startOf('day')
          .diff(moment(startDate).startOf('day'), 'days')
      : 0;
  const rebasedStartDate = new Date(
    moment()
      .tz(timezoneId || moment.tz.guess())
      .format('YYYY-MM-DD HH:mm:ss')
  );

  return {
    startDate: rebasedStartDate,
    endDate: endDate
      ? moment(rebasedStartDate).add(dayDiff, 'days').toDate()
      : null,
  };
};

export function getDaysOfTheWeekFormValue(arr) {
  let dayOfTheWeekObj = {};
  if (arr.length) {
    dayOfTheWeekObj = {
      dayOfTheWeek: arr,
      ...arr.reduce((acc, day) => ({ ...acc, [`repeat-${day}`]: true }), {}),
    };
  }
  return dayOfTheWeekObj;
}

export function parseDonation(donation, user, isRepost, isCopy, patternData) {
  if (!donation) {
    return {};
  }

  const destLocation = parseJsonObject(donation.dest_location);
  const donationLocation = donation?.location ?? {};

  // Use common date calculation function
  const dateObj = calculateDatesForCopy(
    donation.start_date,
    donation.end_date,
    user,
    isCopy,
    patternData,
    donation.timezone_id
  );
  const formDateObj =
    isRepost &&
    dateObj.endDate &&
    moment(dateObj.endDate).isBefore(moment().startOf('day'))
      ? {
          ...dateObj,
          ...rebaseDateRangeToToday(
            dateObj.startDate,
            dateObj.endDate,
            user?.timezone_id
          ),
        }
      : dateObj;

  let dayOfTheWeekObj = getDaysOfTheWeekFormValue(
    parseJsonArray(_get(donation, 'days_of_the_week', '[]'))
  );

  const res = {
    title: donation.title,
    description: donation.description,
    pickupInstructions: donation.pickup_instructions || '',
    dropoffInstructions: donation.dropoff_instructions || '',
    foodTypes: _.mapValues(
      _.keyBy(parseJsonArray(donation.food_weight_object), ({ food_type }) =>
        _.toLower(food_type)
      ),
      ({ estimated_weight, estimated_weight_unit }) => {
        const totalWeight =
          estimated_weight === null
            ? null
            : new Weight(estimated_weight, 'lbs').asRounded(
                estimated_weight_unit
              );
        return {
          value: totalWeight,
          unit: estimated_weight_unit,
          ...(isRepost || isCopy
            ? {
                assignedRecipients: [
                  {
                    teamId: 'unassigned',
                    reservedWeight: totalWeight ?? 0,
                  },
                ],
              }
            : {}),
        };
      }
    ),
    location: {
      lat: donationLocation.lat,
      lng: donationLocation.lng,
      formattedAddress: donationLocation.formatted_address,
      name: donationLocation.place,
      streetAddress: donationLocation.street_address,
      city: donationLocation.city,
      province: donationLocation.province,
      country: donationLocation.country,
      zipCode: donationLocation.zip_code,
      placeId: donationLocation.place_id,
      timeZoneId: donationLocation.timezone_id,
    },
    destLocation:
      isRepost || isCopy
        ? {}
        : {
            lat: destLocation.lat,
            lng: destLocation.lng,
            formattedAddress: destLocation.formatted_address,
            name: destLocation.place,
            streetAddress: destLocation.street_address,
            city: destLocation.city,
            province: destLocation.province,
            country: destLocation.country,
            zipCode: destLocation.zip_code,
            placeId: destLocation.place_id,
            timeZoneId: destLocation.timezone_id,
          },
    images: Array.isArray(donation.images) ? donation.images : [],
    additionalImages: parseJsonArray(donation.additional_images),
    deliveryOption: {
      'SELF-DELIVER': 0,
      'RECEIVER-PICKUP': 1,
      'DELIVERY-BY-FOOD-MOVER': 1, // ie. map to RECEIVER-PICKUP
      'STAFF-MEALS': 4,
    }[donation.delivery_option],
    receiverTeam: isRepost
      ? 'unassigned'
      : (donation.recipient_team_id ??
        donation.receiver_team_id ??
        'unassigned'),
    drivers: donation.drivers ?? [],
    vehicle: _get(donation, 'vehicle_mapped.id', null),
    occurrenceType: isRepost
      ? 'onetime'
      : donation?.is_recurring
        ? patternData?.end_date
          ? 'recurring'
          : 'indefinite'
        : 'onetime',
    interval: _.capitalize(donation.recurring_interval),
    isPartialRescue: Boolean(donation.is_partial),
    estimatedRescueTimeMinutes: donation.estimated_rescue_time_minutes,
    ...formDateObj,
    dayOfTheMonth: {
      isLastDay: donation.is_last_day_of_the_month,
      customDay: donation.day_of_the_month,
      week: {
        number: donation.no_of_the_week_in_month,
        dayOfTheWeek: donation.day_of_the_week_in_month,
      },
    },
    ...dayOfTheWeekObj,
    selectedInventoryItems: Array.isArray(donation.inventory_items)
      ? donation.inventory_items
      : [],
    inventoryItems: (donation.inventory_items ?? []).map((item) => ({
      inventoryItemId: item.inventoryItemId || item.id,
      quantity: item.quantity || 1,
    })),
  };

  if (res.inventoryItems.length) {
    res.inventoryPrefillSnapshot = {
      categories: Object.keys(res.foodTypes),
      foodTypes: res.foodTypes,
      title: res.title,
      description: res.description,
      images: [],
      inventoryItems: res.inventoryItems,
    };
  }

  return res;
}

export function toggleInventoryItem(selectedItems = [], item, savedItems = []) {
  if (selectedItems.some((selected) => selected.id === item.id)) {
    return selectedItems.filter((selected) => selected.id !== item.id);
  }
  const saved = savedItems.find((savedItem) => savedItem.id === item.id);
  return [...selectedItems, { ...item, quantity: saved?.quantity || 1 }];
}

export function isSameInventorySelection(left = [], right = []) {
  const toQuantities = (items) =>
    Object.fromEntries(
      toDonationInventoryPayload(items).map(({ inventoryItemId, quantity }) => [
        inventoryItemId,
        quantity,
      ])
    );
  return _.isEqual(toQuantities(left), toQuantities(right));
}

// Food types (keyed like the form's foodTypes) that the selected inventory
// items add up to.
export function buildInventoryFoodTypes(
  selectedItems,
  { weightUnit = 'lbs', validFoodTypes = null } = {}
) {
  const { categories, weights } = buildInventoryDonationPrefill(selectedItems, {
    weightUnit,
  });
  return Object.fromEntries(
    categories
      .filter(
        (category) => !validFoodTypes || validFoodTypes.includes(category)
      )
      .map((category) => [category, { ...weights[category] }])
  );
}

const foodTypeWeightsMatch = (left, right) => {
  const leftKeys = Object.keys(left ?? {}).sort();
  const rightKeys = Object.keys(right ?? {}).sort();
  return (
    _.isEqual(leftKeys, rightKeys) &&
    leftKeys.every(
      (key) =>
        Number(left[key]?.value || 0) === Number(right[key]?.value || 0) &&
        (left[key]?.unit ?? null) === (right[key]?.unit ?? null)
    )
  );
};

const roundWeight = (value) => Number(Number(value || 0).toFixed(4));

function mergePartialInventoryFoodTypes(foodTypes = {}, inventoryFoodTypes) {
  const merged = {};

  for (const [foodType, { value, unit }] of Object.entries(
    inventoryFoodTypes
  )) {
    const existing = foodTypes[foodType];
    if (!existing) {
      merged[foodType] = {
        value,
        unit,
        minReservableWeight: 1,
        maxReservableWeight: value,
        maxReservableWeightManuallyChanged: false,
        assignedRecipients: [{ teamId: 'unassigned', reservedWeight: value }],
      };
      continue;
    }

    const existingUnit = existing.unit || unit;
    const previousTotal = Number(existing.value || 0);
    const nextTotal = roundWeight(new Weight(value, unit).as(existingUnit));
    const delta = nextTotal - previousTotal;
    const recipients = existing.assignedRecipients ?? [];
    const hasUnassigned = recipients.some(
      ({ teamId }) => teamId === 'unassigned'
    );
    const assignedRecipients = hasUnassigned
      ? recipients.map((recipient) =>
          recipient.teamId === 'unassigned'
            ? {
                ...recipient,
                reservedWeight: Math.max(
                  0,
                  roundWeight(Number(recipient.reservedWeight || 0) + delta)
                ),
              }
            : recipient
        )
      : delta > 0
        ? [...recipients, { teamId: 'unassigned', reservedWeight: delta }]
        : recipients;
    const maxReservableWeight = Number(existing.maxReservableWeight || 0);

    merged[foodType] = {
      ...existing,
      value: nextTotal,
      unit: existingUnit,
      assignedRecipients,
      maxReservableWeight:
        !maxReservableWeight ||
        maxReservableWeight === previousTotal ||
        maxReservableWeight > nextTotal
          ? nextTotal
          : maxReservableWeight,
    };
  }

  for (const [foodType, existing] of Object.entries(foodTypes)) {
    if (merged[foodType]) continue;
    const isHeld = (existing.assignedRecipients ?? []).some(
      (recipient) =>
        recipient.disabled ||
        (recipient.teamId !== 'unassigned' &&
          Number(recipient.reservedWeight || 0) > 0)
    );
    if (isHeld) merged[foodType] = existing;
  }

  return merged;
}

export function syncFoodTypesWithInventory({
  foodTypes,
  appliedFoodTypes,
  savedFoodTypes,
  savedInventoryItems = [],
  selectedItems = [],
  isPartialRescue = false,
  weightUnit,
  validFoodTypes,
}) {
  const unchanged = { foodTypes, appliedFoodTypes };
  if (!foodTypeWeightsMatch(foodTypes, appliedFoodTypes)) return unchanged;

  if (isSameInventorySelection(selectedItems, savedInventoryItems)) {
    return { foodTypes: savedFoodTypes, appliedFoodTypes: savedFoodTypes };
  }

  const inventoryFoodTypes = buildInventoryFoodTypes(selectedItems, {
    weightUnit,
    validFoodTypes,
  });
  if (!Object.keys(inventoryFoodTypes).length) return unchanged;

  const nextFoodTypes = isPartialRescue
    ? mergePartialInventoryFoodTypes(foodTypes, inventoryFoodTypes)
    : inventoryFoodTypes;
  return { foodTypes: nextFoodTypes, appliedFoodTypes: nextFoodTypes };
}

export const useMultipleDriversSchema = () => {
  const { t } = useTranslation('translation');
  return Yup.array()
    .of(
      Yup.object({
        user_id: Yup.string()
          .nullable()
          .required(t('donations.select-a-driver')),
        vehicle_id: Yup.string().nullable().notRequired(),
      }).required(t('donations.select-a-driver'))
    )
    .min(1, t('donations.select-a-driver'))
    .max(3, t('donations.max-3-drivers'))
    .required(t('donations.select-a-driver'));
};

const isRecipientRequired = (data = {}) => {
  return moment(data.endDate).isBefore(moment().startOf('day'));
};

// A donation that has already ended can't be opened up for claiming, so it
// needs a receiver. Shared by the create schema and the receiver field label.
export const isDonationReceiverRequired = ({
  endDate,
  isPartialRescue,
  allowOpenRecipientSelection,
}) =>
  !isPartialRescue &&
  !allowOpenRecipientSelection &&
  isRecipientRequired({ endDate });

export const useDonationFoodTypeSchema = () => {
  const { t } = useTranslation('translation');
  const checkCompletedOnboarding = useCheckCompletedOnboarding();
  const validateTotalWeight =
    ({ operator }) =>
    (value = [], context) => {
      const recipientsWeight = value.reduce(
        (acc, { reservedWeight }) => acc + parseFloat(reservedWeight || '0'),
        0
      );
      const totalWeight = parseFloat(context.options.from[0].value.value || 0);

      const comparators = {
        '<': recipientsWeight < totalWeight,
        '>': recipientsWeight > totalWeight,
      };
      return !(comparators[operator] ?? false);
    };
  const { WEIGHT_UNITS } = useMeasureFieldUnits();
  return ({
    isPartialRescue = false,
    nearbyNPOs = [],
    recipientStatusByTeamId = {},
  } = {}) =>
    Yup.object({
      ...FOOD_TYPES.map(({ value }) => value.toLowerCase()).reduce(
        (foodTypes, foodType) => ({
          ...foodTypes,
          [foodType]: Yup.object({
            value: foodTypeWeightSchema(t),
            unit: Yup.string().oneOf(WEIGHT_UNITS.map(({ value }) => value)),
            ...(isPartialRescue
              ? {
                  minReservableWeight: Yup.number()
                    .typeError(t('form.validation.required'))
                    .min(1, t('form.validation.cant-be-zero'))
                    .max(
                      Yup.ref('maxReservableWeight'),
                      t('donations.min-reservable-weight-exceeded')
                    ),
                  maxReservableWeight: Yup.number()
                    .typeError(t('form.validation.required'))
                    .max(
                      Yup.ref('value'),
                      t('donations.max-reservable-weight-exceeded')
                    ),
                  assignedRecipients: Yup.array()
                    .of(
                      Yup.object({
                        teamId: Yup.string()
                          .test(
                            'required',
                            t('form.validation.required'),
                            (value, context) => {
                              const deliveryOption =
                                context.from[3].value.deliveryOption;
                              const isFoodMover =
                                context.from[3].value.isFoodMover;
                              const endDate = context.from[3].value.endDate;

                              if (
                                isRecipientRequired({
                                  deliveryOption,
                                  endDate,
                                  isFoodMover,
                                })
                              )
                                return value !== 'unassigned';
                              return true;
                            }
                          )
                          .test(
                            'completed-onboarding',
                            ({ value: teamId }) =>
                              checkCompletedOnboarding.buildErrorMessage(
                                nearbyNPOs,
                                teamId
                              ),
                            (teamId) =>
                              checkCompletedOnboarding.validate(
                                teamId,
                                getRecipientOnboardingState({
                                  teamId,
                                  nearbyNPOs,
                                  recipientStatusByTeamId,
                                })
                              )
                          ),
                        reservedWeight: Yup.number().typeError(
                          t('form.validation.number')
                        ),
                      }).required(t('form.validation.required'))
                    )
                    .test(
                      'only-one-unassigned',
                      t('donations.only-one-unassigned-recipient-allowed'),
                      (value = []) => {
                        const unassigned = value.filter(
                          ({ teamId }) => teamId === 'unassigned'
                        );
                        return !(unassigned.length > 1);
                      }
                    )
                    .test(
                      'should-fill-total-weight',
                      t('donations.please-fill-the-complete-amount'),
                      validateTotalWeight({ operator: '<' })
                    )
                    .test(
                      'should-not-exceed-total-weight',
                      t('donations.total-weight-exceeded'),
                      validateTotalWeight({ operator: '>' })
                    ),
                }
              : {}),
          }),
        }),
        {}
      ),
    });
};

export const useGetDaysOfTheWeekValidator = () => {
  const { t } = useTranslation('translation');
  return ({ occurrenceType, interval, ...other }, ctx) => {
    if (
      ['indefinite', 'recurring'].includes(occurrenceType) &&
      ['Weekly', 'Bi-weekly'].includes(interval)
    ) {
      const hasWeekDay = [...'SMTWTFS']
        .map((_, i) => other[`repeat-${i}`])
        .includes(true);
      console.log({ other });
      if (!hasWeekDay) {
        return ctx.createError({
          path: 'dayOfTheWeek',
          message: t('donations.select-at-least-one-day-of-the-week'),
        });
      }
    }
    return true;
  };
};

export const useAddDonationSchema = () => {
  const { user } = useAuth();
  const { t } = useTranslation('translation');
  const foodTypeSchema = useDonationFoodTypeSchema();
  const daysOfTheWeekValidator = useGetDaysOfTheWeekValidator();
  const checkCompletedOnboarding = useCheckCompletedOnboarding();
  const multipleDriversSchema = useMultipleDriversSchema();
  return (
    nearbyNPOs,
    { recipientStatusByTeamId = {}, allowOpenRecipientSelection = false } = {}
  ) =>
    Yup.object({
      title: Yup.string()
        .test(
          'no-email',
          t('form.validation.email-not-allowed'),
          function (value) {
            if (!value) return true;

            // This regex pattern looks for common email patterns
            const emailPattern =
              /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
            return !emailPattern.test(value);
          }
        )
        .required(t('donations.donation-title-required')),
      description: Yup.string().required(t('donations.description-required')),
      isPartialRescue: Yup.boolean().notRequired(),
      foodTypes: Yup.object().when('isPartialRescue', {
        is: true,
        then: () =>
          requireAtLeastOneFoodType(
            foodTypeSchema({
              isPartialRescue: true,
              nearbyNPOs,
              recipientStatusByTeamId,
            }),
            t('donations.at-least-one-food-type')
          ),
        otherwise: () =>
          requireAtLeastOneFoodType(
            foodTypeSchema({ nearbyNPOs, recipientStatusByTeamId }),
            t('donations.at-least-one-food-type')
          ),
      }),
      location: Yup.object({}).when(['deliveryOption'], {
        is: (deliveryOption) => deliveryOption === 1,
        then: () =>
          Yup.object({}).required(t('donations.donation-location-required')),
        otherwise: () => Yup.object({}).optional(),
      }),
      destLocation: Yup.object({}).when(['deliveryOption', 'receiverTeam'], {
        is: (deliveryOption, receiverTeam) =>
          deliveryOption === 0 && receiverTeam && receiverTeam !== 'unassigned',
        then: () =>
          Yup.object({}).required(
            t('donations.donation-destination-location-required')
          ),
        otherwise: () => Yup.object({}).optional(),
      }),
      images: Yup.array().optional(),
      deliveryOption: Yup.number().required(
        t('donations.select-a-delivery-option')
      ),
      receiverTeam: Yup.string()
        .when(['endDate', 'isPartialRescue'], {
          is: (endDate, isPartialRescue) =>
            isDonationReceiverRequired({
              endDate,
              isPartialRescue,
              allowOpenRecipientSelection,
            }),
          then: () =>
            Yup.string()
              .required(t('donations.select-a-receiver'))
              .notOneOf(['unassigned'], t('donations.select-a-receiver')),
          otherwise: () => Yup.string().optional(),
        })
        .test(
          'completed-onboarding',
          ({ value: receiver }) =>
            checkCompletedOnboarding.buildErrorMessage(nearbyNPOs, receiver),
          (receiver) =>
            checkCompletedOnboarding.validate(
              receiver,
              getRecipientOnboardingState({
                teamId: receiver,
                nearbyNPOs,
                recipientStatusByTeamId,
              })
            )
        ),
      drivers: Yup.array().when(['deliveryOption', 'receiverTeam'], {
        is: (deliveryOption, receiverTeam) =>
          deliveryOption === 0 && receiverTeam && receiverTeam !== 'unassigned',
        then: () => multipleDriversSchema,
        otherwise: () => Yup.array().nullable().notRequired(),
      }),
      deliveryRadius: Yup.number().when(['deliveryOption', 'receiverTeam'], {
        is: (deliveryOption, receiverTeam) =>
          (deliveryOption === 0 || deliveryOption === 4) &&
          (!receiverTeam || receiverTeam === 'unassigned'),
        then: (x) =>
          x
            .typeError(t('form.validation.number'))
            .min(0, t('form.validation.positive'))
            .required(t('form.validation.number')),
        otherwise: (x) => x.optional(),
      }),
      occurrenceType: Yup.string()
        .oneOf(['onetime', 'recurring', 'indefinite'])
        .required(t('donations.select-a-occurrence-type')),
      interval: Yup.string().when('occurrenceType', {
        is: (occurrenceType) =>
          ['recurring', 'indefinite'].includes(occurrenceType),
        then: () =>
          Yup.string().required(t('donations.select-a-recurring-interval')),
      }),
      startDate: Yup.string().required(t('donations.select-start-date')),
      endDate: Yup.string()
        .when('occurrenceType', {
          is: 'recurring',
          then: () =>
            Yup.string()
              .required(t('donations.select-end-date'))
              .test(
                'is-date-ranges-correct',
                t('donations.invalid-date-range'),
                (value, context) => {
                  const interval = context?.parent.interval.toLowerCase();

                  if (
                    value &&
                    context.parent.startDate &&
                    moment(value)
                      .startOf('day')
                      .isBefore(moment(context.parent.startDate).startOf('day'))
                  ) {
                    return context.createError({
                      path: 'endDate',
                      message: t('donations.end-date-must-be-after-start-date'),
                    });
                  }

                  if (interval === 'weekly') {
                    const endDate = moment(value).clone().startOf('day');
                    const startDate = moment(context.parent.startDate).startOf(
                      'day'
                    );
                    if (endDate.diff(startDate, 'days') < 7) {
                      return context.createError({
                        path: 'endDate',
                        message: t(
                          'donations.end-date-7-days-after-start-date'
                        ),
                      });
                    }
                  }

                  if (interval === 'bi-weekly') {
                    const endDate = moment(value).clone().startOf('day');
                    const startDate = moment(context.parent.startDate).startOf(
                      'day'
                    );
                    if (endDate.diff(startDate, 'days') < 14) {
                      return context.createError({
                        path: 'endDate',
                        message: t(
                          'donations.end-date-14-days-after-start-date'
                        ),
                      });
                    }
                  }

                  return true;
                }
              ),
        })
        .when('occurrenceType', {
          is: 'indefinite',
          then: () => Yup.date().nullable(),
        }),
      startTime: Yup.date().required(t('donations.select-a-start-time')),
      endTime: Yup.date()
        .required(t('donations.select-a-end-time'))
        .test(
          'is-after-start-time',
          t('donations.end-time-must-be-after-start-time'),
          validateEndTimeAfterStartTime
        ),
      dayOfTheMonth: Yup.object({}).when(['occurrenceType', 'interval'], {
        is: (occurrenceType, interval) =>
          ['indefinite', 'recurring'].includes(occurrenceType) &&
          interval === 'Monthly',
        then: () =>
          Yup.object()
            .shape({
              isLastDay: Yup.bool(),
              customDay: Yup.number().nullable(),
              week: Yup.object()
                .shape({
                  number: Yup.number().nullable(),
                  dayOfTheWeek: Yup.number().nullable(),
                })
                .nullable(),
            })
            .test(
              'atLeastOneNonNull',
              t('donations.select-day-of-the-month'),
              function (value) {
                const { isLastDay, customDay, week } = value;
                return (
                  isLastDay ||
                  customDay ||
                  (_isFinite(week.number) && _isFinite(week.dayOfTheWeek))
                );
              }
            )
            .required(t('donations.select-day-of-the-month')),
        otherwise: () => Yup.object().optional(),
      }),
      estimatedRescueTimeMinutes: Yup.number()
        .typeError(t('form.validation.number'))
        .min(1, t('donations.estimated-rescue-time-minimum'))
        .max(720, t('donations.estimated-rescue-time-maximum'))
        .integer(t('donations.estimated-rescue-time-integer'))
        .when(['deliveryOption'], {
          is: (deliveryOption) =>
            user.rescue_time_tracking_enabled && deliveryOption === 0,
          then: (x) =>
            x.required(t('donations.estimated-rescue-time-required')),
        })
        .nullable(),
    }).test({ test: daysOfTheWeekValidator });
};
